import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ConsultantNavbar from "../../Components/Dashboard/ConsultantDashboard/ConsultantNavbar";
import api from "lib/api/api";

const TYPE_LABELS = {
  AZUBI: "Azubi",
  SKILLED_PROFESSIONAL: "Skilled professional",
  BOTH: "Azubi & skilled professional",
};

const ConsultantEmployerInquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyInquiry, setReplyInquiry] = useState(null);
  const [reply, setReply] = useState({ subject: "", message: "" });
  const [replyStatus, setReplyStatus] = useState({ sending: false, error: "" });
  const [deleteInquiry, setDeleteInquiry] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({ deleting: false, error: "" });
  const [mailAction, setMailAction] = useState({ workingId: null, message: "", error: "" });

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/consultant/employer-inquiries");
      setInquiries(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Employer inquiries could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInquiries(); }, [loadInquiries]);

  const inquiryEmail = (row) => row.email || (row.contact?.includes("@") ? row.contact : "");
  const inquiryPhone = (row) => row.phone || (!row.contact?.includes("@") ? row.contact : "");

  const openReply = (inquiry) => {
    setReplyInquiry(inquiry);
    setReply({ subject: `Your recruitment inquiry | ${inquiry.companyName}`, message: "" });
    setReplyStatus({ sending: false, error: "" });
  };

  const sendReply = async () => {
    if (!reply.subject.trim() || !reply.message.trim()) return;
    setReplyStatus({ sending: true, error: "" });
    try {
      await api.post(`/consultant/employer-inquiries/${replyInquiry.id}/reply`, reply);
      setReplyInquiry(null);
      await loadInquiries();
    } catch (err) {
      setReplyStatus({ sending: false, error: err.response?.data?.message || "The reply could not be sent." });
    }
  };

  const confirmDelete = async () => {
    if (!deleteInquiry) return;
    setDeleteStatus({ deleting: true, error: "" });
    try {
      await api.delete(`/consultant/employer-inquiries/${deleteInquiry.id}`);
      setInquiries((current) => current.filter((item) => item.id !== deleteInquiry.id));
      setDeleteInquiry(null);
      setDeleteStatus({ deleting: false, error: "" });
    } catch (err) {
      setDeleteStatus({ deleting: false, error: err.response?.data?.message || "The inquiry could not be deleted." });
    }
  };

  const retryEmails = async (inquiry) => {
    setMailAction({ workingId: inquiry.id, message: "", error: "" });
    try {
      const response = await api.post(`/consultant/employer-inquiries/${inquiry.id}/retry-emails`);
      const delivered = response.data?.confirmationEmailSent && response.data?.internalNotificationSent;
      setMailAction(delivered
        ? { workingId: null, message: "All recruitment emails were delivered.", error: "" }
        : { workingId: null, message: "", error: "The retry finished, but one or more emails are still pending. Check the recorded error." });
      await loadInquiries();
    } catch (err) {
      setMailAction({ workingId: null, message: "", error: err.response?.data?.message || "The emails could not be retried." });
    }
  };

  const sendMailboxTest = async () => {
    setMailAction({ workingId: "test", message: "", error: "" });
    try {
      await api.post("/consultant/employer-inquiries/test-email");
      setMailAction({ workingId: null, message: "Recruitment mailbox test email sent.", error: "" });
    } catch (err) {
      setMailAction({ workingId: null, message: "", error: err.response?.data?.message || "The mailbox test failed." });
    }
  };

  return (
    <Box sx={{ bgcolor: "#f5f7f8", minHeight: "100vh" }}>
      <ConsultantNavbar />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={900} sx={{ color: "#2D3E50", mb: 1 }}>
          Employer Recruitment Inquiries
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <Typography color="text.secondary">Requests submitted through the public corporate recruitment form.</Typography>
          <Button variant="outlined" onClick={sendMailboxTest} disabled={mailAction.workingId === "test"}>
            {mailAction.workingId === "test" ? "Sending test..." : "Test recruitment mailbox"}
          </Button>
        </Box>
        {error && <Alert severity="error" action={<button onClick={loadInquiries}>Retry</button>} sx={{ mb: 2 }}>{error}</Alert>}
        {mailAction.message && <Alert severity="success" sx={{ mb: 2 }}>{mailAction.message}</Alert>}
        {mailAction.error && <Alert severity="error" sx={{ mb: 2 }}>{mailAction.error}</Alert>}
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Table sx={{ minWidth: 1050 }}>
            <TableHead sx={{ bgcolor: "#f8f9fa" }}>
              <TableRow>
                <TableCell><strong>Company / Contact</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Looking for</strong></TableCell>
                <TableCell><strong>Position / Industry</strong></TableCell>
                <TableCell><strong>Preferred profile</strong></TableCell>
                <TableCell><strong>Message</strong></TableCell>
                <TableCell><strong>Received</strong></TableCell>
                <TableCell><strong>Email delivery</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : inquiries.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6 }}>No employer inquiries found.</TableCell></TableRow>
              ) : inquiries.map((row) => (
                <TableRow key={row.id} hover sx={{ verticalAlign: "top" }}>
                  <TableCell><Typography fontWeight={800}>{row.companyName}</Typography><Typography variant="body2">{row.contactPerson}</Typography></TableCell>
                  <TableCell><Box component={inquiryEmail(row) ? "a" : "span"} href={inquiryEmail(row) ? `mailto:${inquiryEmail(row)}` : undefined} sx={{ color: inquiryEmail(row) ? "primary.main" : "text.primary", fontWeight: 600 }}>{inquiryEmail(row) || "-"}</Box></TableCell>
                  <TableCell><Box component={inquiryPhone(row) ? "a" : "span"} href={inquiryPhone(row) ? `tel:${inquiryPhone(row).replace(/[^+\d]/g, "")}` : undefined} sx={{ color: inquiryPhone(row) ? "primary.main" : "text.primary", fontWeight: 600, direction: "ltr" }}>{inquiryPhone(row) || "-"}</Box></TableCell>
                  <TableCell><Chip label={TYPE_LABELS[row.candidateType] || row.candidateType} size="small" color="primary" variant="outlined" /></TableCell>
                  <TableCell>{row.positionOrIndustry}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>{row.preferredProfile || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>{row.message || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}</TableCell>
                  <TableCell sx={{ minWidth: 190 }}>
                    <Chip label={row.confirmationEmailSent ? "Applicant confirmed" : "Applicant email pending"} color={row.confirmationEmailSent ? "success" : "warning"} size="small" sx={{ mb: 0.75 }} />
                    <br />
                    <Chip label={row.internalNotificationSent ? "Theresa notified" : "Theresa notification pending"} color={row.internalNotificationSent ? "success" : "warning"} size="small" />
                    {row.lastEmailError && <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>{row.lastEmailError}</Typography>}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {row.lastReplyAt && <Chip label={`Replied ${new Date(row.lastReplyAt).toLocaleDateString()}`} color="success" size="small" sx={{ mb: 1 }} />}
                    <Button size="small" variant="outlined" onClick={() => openReply(row)} disabled={!inquiryEmail(row)}>
                      Reply by email
                    </Button>
                    {(!row.confirmationEmailSent || !row.internalNotificationSent) && (
                      <Button size="small" onClick={() => retryEmails(row)} disabled={mailAction.workingId === row.id} sx={{ mt: 1 }}>
                        {mailAction.workingId === row.id ? "Retrying..." : "Retry pending emails"}
                      </Button>
                    )}
                    <Button size="small" color="error" onClick={() => { setDeleteInquiry(row); setDeleteStatus({ deleting: false, error: "" }); }} sx={{ mt: 1 }}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      <Dialog open={Boolean(replyInquiry)} onClose={() => !replyStatus.sending && setReplyInquiry(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reply to {replyInquiry?.companyName}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Email: {replyInquiry ? inquiryEmail(replyInquiry) : ""}</Typography>
          {replyStatus.error && <Alert severity="error" sx={{ mb: 2 }}>{replyStatus.error}</Alert>}
          <TextField fullWidth required label="Subject" value={reply.subject} onChange={(e) => setReply((current) => ({ ...current, subject: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth required multiline minRows={7} label="Message" value={reply.message} onChange={(e) => setReply((current) => ({ ...current, message: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReplyInquiry(null)} disabled={replyStatus.sending}>Cancel</Button>
          <Button variant="contained" onClick={sendReply} disabled={replyStatus.sending || !reply.subject.trim() || !reply.message.trim()}>
            {replyStatus.sending ? "Sending..." : "Send email reply"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(deleteInquiry)} onClose={() => !deleteStatus.deleting && setDeleteInquiry(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete employer inquiry?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the inquiry from <strong>{deleteInquiry?.companyName}</strong>. This action cannot be undone.</Typography>
          {deleteStatus.error && <Alert severity="error" sx={{ mt: 2 }}>{deleteStatus.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteInquiry(null)} disabled={deleteStatus.deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleteStatus.deleting}>
            {deleteStatus.deleting ? "Deleting..." : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultantEmployerInquiriesPage;
